<template>
  <div class="promotions-list">
    <TotalInfoBlock v-if="paginationData?.total" :total="parseInt(paginationData?.total)" />

    <template v-if="isMobileDevice">
      <div v-if="items.length" class="flex flex-col gap-3">
        <div
          v-for="item of items"
          :key="item.id"
          class="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          @click="onClickPromotion(item.id)"
        >
          <div class="text-sm text-gray-500">{{ $t("page.users.id") }}: {{ item.id }}</div>
          <div class="mt-1 font-medium">{{ item.name }}</div>
          <div class="mt-2 text-sm text-gray-600">
            {{ $t("promotions.createdAt") }}: {{ item.createdAt }}
          </div>
          <div class="mt-1 text-sm text-gray-600">
            {{ $t("promotions.validityPeriod") }}: {{ item.validityPeriod }}
          </div>
          <OurTag class="mt-2" size="md" :text="item.status?.text" :color="item.status?.color" />
          <div
            v-if="havePermissionEdit || havePermissionDelete"
            class="mt-3 flex justify-end gap-2"
            @click.stop
          >
            <OurSvgIcon
              v-if="havePermissionDelete"
              interactive
              :src="icons.Trash"
              color="gray"
              variant="light"
              :tooltip="$t('button.delete')"
              @click="onClickDelete(item.id)"
            />
            <OurSvgIcon
              v-if="havePermissionEdit"
              interactive
              :src="icons.Edit"
              color="gray"
              variant="light"
              :tooltip="$t('button.edit')"
              @click="onClickEdit(item.id)"
            />
          </div>
        </div>
      </div>
      <SkeletonLoaderCard v-else :table-headers="tableHeaders" />
    </template>

    <OurTable
      v-else
      :headers="tableHeaders"
      :items="items"
      :table-fixed="false"
      class="promotions-list-table"
      @clickRow="onClickRow"
    >
      <template #cell-status="{ value }">
        <OurTag size="md" :text="value?.text" :color="value?.color" />
      </template>

      <template #cell-edit="{ value }">
        <div
          v-if="havePermissionEdit || havePermissionDelete"
          class="flex items-center justify-end gap-2"
        >
          <OurSvgIcon
            v-if="havePermissionDelete"
            interactive
            :src="icons.Trash"
            color="gray"
            size="sm"
            variant="light"
            :tooltip="$t('button.delete')"
            @click.stop="onClickDelete(value)"
          />
          <OurSvgIcon
            v-if="havePermissionEdit"
            interactive
            :src="icons.Edit"
            color="gray"
            size="sm"
            variant="light"
            :tooltip="$t('button.edit')"
            @click.stop="onClickEdit(value)"
          />
        </div>
      </template>
    </OurTable>

    <OurPagination
      v-if="isMoreThanOnePage"
      :current-page="paginationData.currentPage"
      :per-page="paginationData.perPage"
      :total="paginationData.total"
      :disabled="isLoading"
      @pageChange="onPageChange"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import TotalInfoBlock from "@/components/TotalInfoBlock.vue";

export default {
  name: "PromotionsList",

  components: { SkeletonLoaderCard, TotalInfoBlock },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: () => ({}),
    },

    havePermissionEdit: {
      type: Boolean,
      default: false,
    },

    havePermissionDelete: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),

    icons: () => ({
      Edit: require("@/components/_bit/IconSvg/icons/Edit.svg"),
      Trash: require("@/components/_bit/IconSvg/icons/Trash.svg"),
    }),

    tableHeaders() {
      const headers = [
        {
          value: "id",
          text: this.$t("page.users.id"),
          thClass: "w-24",
          tdClass: "max-w-[5rem] min-w-[5rem]",
        },
        {
          value: "name",
          text: this.$t("promotions.name"),
          thClass: "w-[14rem]",
          tdClass: "max-w-[14rem] min-w-[10rem]",
        },
        {
          value: "createdAt",
          text: this.$t("promotions.createdAt"),
          thClass: "w-[10rem]",
          tdClass: "max-w-[12rem] min-w-[10rem]",
        },
        {
          value: "validityPeriod",
          text: this.$t("promotions.validityPeriod"),
          thClass: "w-[14rem]",
          tdClass: "max-w-[16rem] min-w-[12rem]",
        },
        {
          value: "status",
          text: this.$t("page.dealersCompany.status"),
          thClass: "w-[10rem]",
          tdClass: "max-w-[12rem] min-w-[10rem]",
        },
      ];

      if (this.havePermissionEdit || this.havePermissionDelete) {
        headers.push({
          value: "edit",
          text: "",
          thClass: "w-[8rem]",
          tdClass: "max-w-[8rem] min-w-[8rem]",
        });
      }

      return headers;
    },

    isMoreThanOnePage() {
      return this.paginationData.lastPage > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },
  },

  watch: {
    routePage(newPage) {
      if (newPage !== this.paginationData.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  methods: {
    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    onClickRow(row) {
      this.onClickPromotion(row.id);
    },

    onClickPromotion(id) {
      this.$emit("clickPromotion", id);
    },

    onClickEdit(id) {
      this.$emit("clickEdit", id);
    },

    onClickDelete(id) {
      this.$emit("clickDelete", id);
    },
  },
};
</script>
